import type { FCWithChildren } from '@pbk-web/types';
import { dataAttributeProps } from '@pbk-web/utils';
import type { JSX, ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import Button, { ButtonSize, ButtonType } from '../../buttons/Button';
import PlusIcon from '../../icons/misc/PlusIcon';
import { Heading, HeadingSize } from '../../text/Heading';
import Tooltip from '../../tooltip/Tooltip';
import { Modal } from '../Modal';

type Props = {
  title?: string | JSX.Element;
  subTitle?: string | JSX.Element;
  titleIcon?: ReactElement;
  onTitleIconClick?: () => void;
  headerAction?: ReactNode;
  onCancelClick?: () => void;
  onConfirmClick?: () => void;
  onTertiaryButtonClick?: () => void;
  confirmDisabled?: boolean;
  confirmDisabledTooltip?: string;
  confirmLoading?: boolean;
  confirmButtonTitle?: string | null;
  cancelButtonTitle?: string | null;
  tertiaryButtonTitle?: string | null;
  tertiaryButtonIcon?: ReactElement | null;
  /** A line of secondary text in the footer, beside the tertiary button and opposite the
   *  confirm/cancel pair — a keyboard shortcut, a note about what the action costs. */
  footerNote?: ReactNode;
  alt?: boolean;
  uncloseable?: boolean;
  bare?: boolean;
};

const StandardModal: FCWithChildren<Props> = (props) => {
  const { t } = useTranslation('common');
  const {
    title,
    subTitle,
    titleIcon,
    onTitleIconClick = props.onCancelClick,
    headerAction,
    onCancelClick,
    onConfirmClick,
    onTertiaryButtonClick,
    confirmDisabled,
    confirmDisabledTooltip,
    confirmLoading,
    cancelButtonTitle = t('modals.cancel'),
    confirmButtonTitle = t('modals.save'),
    tertiaryButtonTitle,
    tertiaryButtonIcon = <PlusIcon className="h-3 w-3" />,
    footerNote,
    alt,
    uncloseable,
    children,
    bare,
  } = props;

  return (
    // `data-testid="modal"` is the default identity; a caller that passes its own `data-*` (e.g. a distinct
    // `data-testid` so stacked dialogs are tellable apart in E2E) overrides it via the spread.
    <Modal data-testid="modal" {...dataAttributeProps(props)} uncloseable={uncloseable}>
      <Modal.Header align="left" underline={(!!title || !!subTitle) && !bare}>
        <div className="flex items-start gap-1">
          <div className="mt-1 -ml-3 flex cursor-pointer items-start empty:hidden" onClick={onTitleIconClick}>
            {titleIcon}
          </div>
          <div className="max-w-full min-w-0">
            {title && (
              <Tooltip text={title} truncatedTextMode>
                {(tooltip) => (
                  <div {...tooltip} className="mr-2 max-w-full min-w-0">
                    <Heading size={HeadingSize.H5} className="truncate font-medium">
                      {title}
                    </Heading>
                  </div>
                )}
              </Tooltip>
            )}
            {subTitle && (
              <>
                {/* Tooltip separator */}
                <span className="hidden"> - </span>

                <div className="text-pbk-16 text-text-4 py-1 font-normal">{subTitle}</div>
              </>
            )}
          </div>
          {headerAction}
        </div>
      </Modal.Header>
      <Modal.Body padding={bare ? 'p-0' : 'px-6 py-4'}>{children}</Modal.Body>
      <Modal.Footer
        justification="justify-between"
        padding={!!onTertiaryButtonClick || !!onCancelClick || !!onConfirmClick || !!footerNote ? /* default */ undefined : 'p-0'}
        topline={!!onTertiaryButtonClick || !!onCancelClick || !!onConfirmClick || !!footerNote}
      >
        {/* `min-w-0` so a note wraps rather than pushing the buttons off the row. */}
        <div className="flex min-w-0 items-center gap-3">
          {onTertiaryButtonClick && tertiaryButtonTitle && (
            <Button type={ButtonType.TERTIARY} size={ButtonSize.S} onClick={onTertiaryButtonClick} data-testid="modal-tertiary">
              {tertiaryButtonIcon && <Button.Slot name="Icon">{tertiaryButtonIcon}</Button.Slot>}
              {tertiaryButtonTitle}
            </Button>
          )}
          {footerNote}
        </div>
        <div className={`flex gap-4 ${alt && 'flex-row-reverse'}`}>
          {onCancelClick && cancelButtonTitle && (
            <Button
              type={alt ? ButtonType.PRIMARY : ButtonType.SECONDARY}
              size={ButtonSize.S}
              onClick={onCancelClick}
              data-testid="modal-cancel"
              disabled={confirmLoading}
            >
              {cancelButtonTitle}
            </Button>
          )}
          {onConfirmClick && confirmButtonTitle && (
            <Button
              type={alt ? ButtonType.SECONDARY : ButtonType.PRIMARY}
              size={ButtonSize.S}
              disabled={confirmDisabled}
              onClick={onConfirmClick}
              loading={confirmLoading}
              data-testid="modal-done"
              title={confirmDisabled ? confirmDisabledTooltip : undefined}
            >
              {confirmButtonTitle}
            </Button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default StandardModal;
